/**
 * LegalPage — shared renderer for the compliance pages at /privacy-policy
 * and /sms-terms. Renders build-time HTML from the legal manifest; no
 * runtime markdown parsing, same pattern as AdventureLogPostPage.
 *
 * These paths are quoted verbatim in outbound SMS and were submitted to
 * HighLevel for A2P 10DLC registration. Do not rename or redirect them,
 * and do not reword the copy — see docs/agent/legal-pages.md.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import Reveal from '../components/ui/Reveal.jsx';
import { getLegalPage } from '../content/legal-manifest.js';

/** The other legal page, for the cross-link at the foot of each document. */
const SIBLING = {
  'privacy-policy': { to: '/sms-terms', label: 'Read the SMS Program Terms' },
  'sms-terms': { to: '/privacy-policy', label: 'Read the Privacy Policy' },
};

export default function LegalPage({ slug }) {
  const page = getLegalPage(slug);

  if (!page) {
    // A missing manifest entry is a build fault, not a visitor error — surface
    // it loudly during SSR so prerender doesn't quietly ship an empty page.
    if (typeof window === 'undefined') {
      console.warn(`[ssr] LegalPage: no legal page found for slug "${slug}"`);
    }
    return (
      <>
        <Helmet>
          <title>Page not found — The Artificer's AI</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <section className="section-pad bg-surface text-ink-50">
          <div className="container-page max-w-prose">
            <h1 className="text-display-lg font-display font-semibold mb-4">Page not found</h1>
            <p className="text-body text-ink-200 mb-8">
              Could not find legal page: <code>"{slug}"</code>
            </p>
            <Link
              to="/"
              className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors"
            >
              Back to home
            </Link>
          </div>
        </section>
      </>
    );
  }

  const sibling = SIBLING[slug];

  return (
    <>
      <Helmet>
        <title>{page.title} — The Artificer's AI</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={page.canonical} />
        <meta property="og:title" content={`${page.title} — The Artificer's AI`} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={page.canonical} />
      </Helmet>

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="header" delay={0} className="mb-10">
            <h1 className="text-display-lg font-display font-semibold mb-4">
              {page.title}
            </h1>
            <p className="text-body text-ink-300">
              Effective date: {page.effectiveDateDisplay}
            </p>
          </Reveal>

          {/* page.html is generated at build time from trusted markdown in
              src/content/legal/; no user input enters this path. */}
          <div
            className="post-body"
            dangerouslySetInnerHTML={{ __html: page.html }}
          />

          {sibling && (
            <Reveal as="div" delay={0} className="mt-12 pt-8 border-t border-white/[.08]">
              <Link
                to={sibling.to}
                className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors"
              >
                {sibling.label}
              </Link>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}

export function PrivacyPolicyPage() {
  return <LegalPage slug="privacy-policy" />;
}

export function SmsTermsPage() {
  return <LegalPage slug="sms-terms" />;
}
